import React from 'react';
import PropTypes from 'prop-types';
import produce from 'immer';
import moment from 'moment';
import 'moment-range';
import { Box, Inline } from '@styleseat/ui';
import ssPropTypes from '../../../modules/ssPropTypes';
import Calendar from './Calendar';
import DatePickerHeader from './Header';
import './BaseDatePicker.scss';
import {
  BodyText,
  Stack,
} from '..';
import { useIsMobile } from '../../../hooks';

const WeekNames = () => {
  const isMobile = useIsMobile();

  return (
    <Inline alignItems="center" justifyContent="space-evenly">
      {['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'].map(label => (
        <Box key={label} minWidth={isMobile ? 36 : 40}>
          <BodyText textAlign="center" weight="bold">{label[0]}</BodyText>
        </Box>
      ))}
    </Inline>
  );
};

/**
 * Determine whether or not to disable navigation to the previous month.
 * @param {moment} currentDate The start of the current month
 */
function checkPreviousMonthDisable(currentDate) {
  return currentDate.isSameOrBefore(moment(), 'month');
}

/**
 * Determine whether or not to disable navigation to the next month.
 * @param {moment} currentDate The start of the current month
 */
function checkNextMonthDisable(currentDate) {
  return currentDate.isSameOrAfter(moment(), 'month');
}

/**
 * Renders a date picker, displaying the days in a single month at a time and supporting selection
 * of a set of dates. Also supports user interaction by clicking on individual date boxes and
 * navigating between months.
 *
 * Any props provided that are not specified in propTypes are passed directly to DateCalendar.
 *
 * @example
 * import BaseDatePicker from 'components/ui/date-picker/BaseDatePicker';
 *
 * const MyView = () => {
 *   return (
 *     <BaseDatePicker
 *       startDate={moment().startOf('month')}
 *       disablePastDates={false}
 *       selectedDates={{[moment().format('YYYY-MM-DD')]: true}}
 *       onMonthChange={(momentValue) => console.log('Day changed')}
 *     />
 *   );
 * }
 */
class BaseDatePicker extends React.Component {
  static propTypes = {
    startDate: ssPropTypes.moment,
    disablePastDates: PropTypes.bool,
    disableFutureDates: PropTypes.bool,
    /* map from date YYYY-MM-DD string to boolean */
    selectedDates: PropTypes.object,
    /* receives moment representing the start of the first day of the new month */
    onMonthChange: PropTypes.func,
    /* Any other props supplied are passed directly to DateCalendar */
    currentDate: ssPropTypes.moment,
    selectedColor: PropTypes.string,
    selectedTextColor: PropTypes.string,
    /** highlight today's date */
    selectToday: PropTypes.bool,
  };

  static defaultProps = {
    startDate: moment().startOf('month'),
    disablePastDates: true,
    disableFutureDates: false,
    onMonthChange: () => { },
    selectedDates: {},
    currentDate: null,
    selectedColor: null,
    selectedTextColor: null,
    selectToday: true,
  };

  state = {
    disablePreviousMonth: false,
    disableNextMonth: false,
    currentDate: moment().startOf('month'),
    dateProps: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      disablePreviousMonth: props.disablePastDates
        && checkPreviousMonthDisable(props.startDate),
      disableNextMonth: props.disableFutureDates
        && checkNextMonthDisable(props.startDate),
      currentDate: props.startDate.startOf('month'),
      dateProps: {},
    };
  }

  componentDidMount() {
    this.updateDateProps();
  }

  componentDidUpdate(prevProps, prevState) {
    // Make sure we haven't already updated dateProps and that we've updated something that could
    // affect date props
    if (
      prevState.dateProps === this.state.dateProps
      && (prevProps.selectedDates !== this.props.selectedDates
        || !prevState.currentDate.isSame(this.props.currentDate))
    ) {
      this.updateDateProps();
    }
  }

  /**
   * Handles the DatePickerHeader.onNextMonthClick event
   */
  onGoToNextMonth = () => {
    const { currentDate } = this.state;

    this.handleMonthChange(currentDate.clone().add(1, 'month'));
  };

  /**
   * Handles the DatePickerHeader.onPreviousMonthClick event
   */
  onGoToPreviousMonth = () => {
    const { currentDate } = this.state;

    this.handleMonthChange(currentDate.clone().subtract(1, 'month'));
  };

  /**
   * Handles updating of state from one month to another. Fires the onMonthChange event.
   * @param {moment} newDate The new start of the month
   */
  handleMonthChange(newDate) {
    const { onMonthChange } = this.props;

    this.setState({
      currentDate: newDate,
      disablePreviousMonth: this.props.disablePastDates
        && checkPreviousMonthDisable(newDate),
      disableNextMonth: this.props.disableFutureDates
        && checkNextMonthDisable(newDate),
    }, () => onMonthChange(newDate));
  }

  /**
   * Gets the first last dates of the selected range
   * @returns {Object} {selectedStartDate, selectedEndDate}
   */
  getFirstAndLastSelectedDates() {
    const {
      selectedDates,
    } = this.props;

    if (!selectedDates || !Object.keys(selectedDates).length) {
      return {
        selectedStartDate: null,
        selectedEndDate: null,
      };
    }
    const orderedKeys = Object.keys(selectedDates).sort();

    return {
      selectedStartDate: orderedKeys[0],
      selectedEndDate: orderedKeys[orderedKeys.length - 1],
    };
  }

  /**
   * Build the map of date disabled flags
   * @param {moment} date The beginning of the month on which to perform calculations.
   */
  updateDateProps() {
    const {
      disablePastDates,
      disableFutureDates,
      selectedDates,
      selectedColor,
      selectedTextColor,
      selectToday,
    } = this.props;
    const { currentDate } = this.state;
    let { dateProps } = this.state;
    const now = moment();
    const days = moment
      .range(currentDate.clone().startOf('month'), currentDate.clone().endOf('month'))
      .toArray('days');
    const { selectedStartDate, selectedEndDate } = this.getFirstAndLastSelectedDates();

    dateProps = produce(dateProps, draftDateProps => {
      days.forEach(day => {
        // disabling this warning because this is how we're actually supposed to use immer
        // eslint-disable-next-line no-param-reassign
        draftDateProps[day.date()] = {
          disabled: (disablePastDates && day.isBefore(now, 'date'))
            || (disableFutureDates && day.isAfter(now, 'date')),
          selected: !!selectedDates[day.format('YYYY-MM-DD')],
          startRange: selectedStartDate && day.isSame(selectedStartDate, 'date'),
          endRange: selectedEndDate && day.isSame(selectedEndDate, 'date'),
          selectedColor,
          selectedTextColor,
          selectToday,
        };
      });
    });

    this.setState({ dateProps });
  }

  render() {
    const {
      currentDate,
      disablePreviousMonth,
      disableNextMonth,
      dateProps,
    } = this.state;

    return (
      <Stack space={16}>
        <DatePickerHeader
          currentDate={currentDate || this.props.startDate}
          onPreviousMonthClick={this.onGoToPreviousMonth}
          onNextMonthClick={this.onGoToNextMonth}
          disablePreviousMonth={disablePreviousMonth}
          disableNextMonth={disableNextMonth}
        />
        <WeekNames />
        <Calendar
          {...this.props}
          startDate={currentDate || this.props.startDate}
          dateProps={dateProps}
        />
      </Stack>
    );
  }
}

export default BaseDatePicker;
